import React, { useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import {
  TextField,
  Card,
  CardContent,
  Typography,
  FormControlLabel,
  Checkbox,
  Collapse,
  RadioGroup,
  Radio,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  CircularProgress,
  Box,
  makeStyles,
} from '@material-ui/core';
import { CheckCircle, RadioButtonUnchecked } from '@material-ui/icons';
import messages from 'containers/PreOpeningPage/messages';
import { translate, arrayTranslate } from 'containers/services/translate';
import { pushTo } from 'containers/services/routes-helper';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { PAGES } from 'routes';
import { CLAIM, CLAIM_STATUS } from 'containers/data-structure';
import { find, get, isEmpty } from 'lodash';
import { Listings, Config, SagisConfig } from 'containers/AppContainer/typings';
import { CHOICE } from 'containers/PreOpeningPage/constants';
import ClaimCircumstancesDrawer from 'containers/RecapManagerPage/ClaimCircumstancesDrawer';
import validate from 'containers/services/validateForm';
import { displayErrorText } from 'containers/services/validateForm';
import {
  MANAGEMENT_MODES,
  SCALE_CASES_KEY,
} from 'containers/RecapManagerPage/constants';
import { GridWrapper } from 'containers/PreOpeningPage/ClaimInformation/style';
import { Container } from 'containers/PreOpeningPage/styled-component';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { PROCEDURE_TYPES } from 'containers/LawyerLegalProceedingsPage/constants';
import { validationCourtFileNumber } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/utils';
import SearchIcon from '@material-ui/icons/Search';
import { useDispatch, useSelector } from 'react-redux';
import { findProvisionalClaimByReference } from 'containers/PreOpeningPage/actions';
import { makeSelectProvisionalClaimReference } from 'containers/ProvisionalClaimRecap/selectors';

export const claimCircumstancesConstraints = (claim: any) => {
  return {
    [CLAIM.RESPONSABILITY_SHARE]: {
      presence: true,
    },
    [CLAIM.ACCIDENT_DESCRIPTION]: {
      presence: true,
    },
    [CLAIM.IS_JUDICIAL_PROCEDURE]: {
      presence: true,
    },
    [CLAIM.SCALE_CASE_KEY]: {
      presence: true,
    },
    [CLAIM.ACCIDENT_CIRCONSTANCE_KEY]: {
      presence: true,
    },
    [CLAIM.RESPONSABILITY_KEY]: {
      presence: true,
    },
    [CLAIM.JUDICIAL_PROCEDURE_ID]: {
      presence: get(claim, [CLAIM.IS_JUDICIAL_PROCEDURE], '') === CHOICE.YES,
    },
    [CLAIM.PROVISIONAL_CLAIM_REFERENCE]: {
      presence: get(claim, [CLAIM.IS_PROVISIONAL], '') === CHOICE.YES,
    },
  };
};

export const claimCircumstancesValidator = (claim: any) => {
  return validate(claim, claimCircumstancesConstraints(claim));
};

export interface ClaimCircumstancesProps {
  intl: IntlShape;
  updateFilde?: Function;
  claimToPreopen: any;
  inputProps?: any;
  disableInput?: boolean;
  inputLabelProps?: boolean;
  listings?: Listings;
  storedClaimToPreopen?: any;
  setClaimRecap?: Function;
  claimCircumstancesValidation?: any;
  isRecapPage: boolean;
  isManager: boolean;
  isReadOnly: boolean;
  claimCircumstancesSagis: any;
  isBasicManagement?: boolean;
  procedureType?: any;
  procedureNatureId?: any;
  handleChangeProcedureType?: any;
  handleChangeProcedureNature?: any;
  location?: any;
  setIsSearchExecuted?: any;
  isSearchExecuted?: any;
  setClaim: Function;
  isConsultation?: boolean;
}
const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    padding: 12,
  },
}));
const ClaimCircumstances: React.FC<ClaimCircumstancesProps> = (
  props,
): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const provisionalClaimReference = useSelector(
    makeSelectProvisionalClaimReference(),
  );
  const [isScaleCaseOther, updateIsScaleCaseOther] = useState(false);
  const [isCourtNumberFieldChanged, setIsCourtNumberFieldChanged] = useState(
    false,
  );
  const [
    isProvisionalReferenceChanged,
    setIsProvisionalReferenceChanged,
  ] = useState(false);
  const inputLabel = React.useRef<HTMLLabelElement>(null);
  const isPreOpeningManager = userPrivileges.userHasPrivilege(
    PRIVILEGES.ISPREOPENMANAGER,
  );
  const getCircumstances = () => {
    let circumstances = get(props.listings, 'circumstance', []).filter(
      (circumstance: SagisConfig) =>
        circumstance.ItemGroup === claim[CLAIM.SCALE_CASE_KEY],
    );
    return circumstances;
  };

  const claimToPreopen = !isEmpty(props.storedClaimToPreopen)
    ? { ...props.storedClaimToPreopen }
    : { ...props.claimToPreopen };

  const isPending =
    get(claimToPreopen, 'claim.status') === CLAIM_STATUS.PENDING;

  const handleChange = (field: string) => (
    event: React.ChangeEvent<{ name?: string; value: any }>,
  ) => {
    const { value } = event.target;

    const claimToPreopen = !isEmpty(props.storedClaimToPreopen)
      ? { ...props.storedClaimToPreopen }
      : { ...props.claimToPreopen };

    const { claim } = claimToPreopen;
    switch (field) {
      case CLAIM.IS_JUDICIAL_PROCEDURE:
        claim[CLAIM.IS_JUDICIAL_PROCEDURE] = value;
        if (value === CHOICE.NO) claim[CLAIM.JUDICIAL_PROCEDURE_ID] = null;
        break;
      case CLAIM.JUDICIAL_PROCEDURE_ID:
        claim[CLAIM.JUDICIAL_PROCEDURE_ID] = parseInt(value);
        break;
      case CLAIM.SCALE_CASE_KEY:
        claim[CLAIM.ACCIDENT_CIRCONSTANCE_KEY] = '';
        claim[CLAIM.ACCIDENT_CIRCONSTANCE] = '';
        claim[CLAIM.RESPONSABILITY_KEY] = '';
        claim[CLAIM.RESPONSABILITY_SHARE] = '';
        claim[CLAIM.SCALE_CASE_KEY] = value;
        if (value === SCALE_CASES_KEY.OTHER) {
          updateIsScaleCaseOther(true);
        } else {
          updateIsScaleCaseOther(false);
        }
        break;
      case CLAIM.ACCIDENT_CIRCONSTANCE_KEY: {
        claim[CLAIM.ACCIDENT_CIRCONSTANCE] = '';
        claim[CLAIM.RESPONSABILITY_KEY] = '';
        claim[CLAIM.RESPONSABILITY_SHARE] = '';
        claim[CLAIM.ACCIDENT_CIRCONSTANCE_KEY] = value;
        const expectedCircumstance = get(
          props.listings,
          'circumstance',
          [],
        ).find((circumstance: SagisConfig) => circumstance.ItemKey === value);

        claim[CLAIM.RESPONSABILITY_KEY] = expectedCircumstance
          ? expectedCircumstance.responsablityRateCode
          : '';
        if (isScaleCaseOther) {
          claim[CLAIM.ACCIDENT_CIRCONSTANCE] = expectedCircumstance
            ? expectedCircumstance.ItemDescription
            : '';
        }
        const responsability = find(
          get(props.listings, 'responsability'),
          (element: { ItemKey: any }) =>
            element.ItemKey === claim[CLAIM.RESPONSABILITY_KEY],
        );
        claim[CLAIM.RESPONSABILITY_SHARE] = get(
          responsability,
          'ItemDescription',
          '',
        );
        break;
      }

      case CLAIM.RESPONSABILITY_KEY:
        claim[CLAIM.RESPONSABILITY_KEY] = value;
        const responsability = find(
          get(props.listings, 'responsability'),
          (element: { ItemKey: any }) =>
            element.ItemKey === claim[CLAIM.RESPONSABILITY_KEY],
        );
        claim[CLAIM.RESPONSABILITY_SHARE] = get(
          responsability,
          'ItemDescription',
          '',
        );
        break;
      case CLAIM.ACCIDENT_DESCRIPTION:
        claim[CLAIM.ACCIDENT_DESCRIPTION] = value;
        break;
      case CLAIM.MANAGEMENT_MODE:
        claim[CLAIM.MANAGEMENT_MODE] = value;
        break;
      case CLAIM.PROCEDURE_NATURE_ID:
        claim[CLAIM.PROCEDURE_NATURE_ID] = value;
        break;
      case CLAIM.COURT_NUMBER:
        setIsCourtNumberFieldChanged(true);
        claim[CLAIM.COURT_NUMBER] = value;
        break;
      case CLAIM.IS_PROVISIONAL:
        setIsProvisionalReferenceChanged(true);
        claim[CLAIM.IS_PROVISIONAL] = value;
        break;
      case CLAIM.PROVISIONAL_CLAIM_REFERENCE:
        props.setIsSearchExecuted(false);
        setIsProvisionalReferenceChanged(true);
        claim[CLAIM.PROVISIONAL_CLAIM_REFERENCE] = value;
        claim[CLAIM.IS_PROVISIONAL] = CHOICE.YES;
        break;
      default:
        return claimToPreopen;
    }

    props.updateFilde && props.updateFilde(claimToPreopen);
    props.setClaimRecap && props.setClaimRecap(claimToPreopen);
  };

  let value = featureService.hasFeature(FEATURES.COURT_CALENDAR)
    ? (!isEmpty(props.storedClaimToPreopen) &&
        !props.isSearchExecuted &&
        !props.isRecapPage) ||
      (!isEmpty(props.storedClaimToPreopen) && props.isRecapPage)
      ? { ...props.storedClaimToPreopen }
      : props.claimToPreopen
    : !isEmpty(props.storedClaimToPreopen)
    ? { ...props.storedClaimToPreopen }
    : props.claimToPreopen;

  const { claim } = value;

  if (
    !props.isRecapPage &&
    provisionalClaimReference &&
    !isProvisionalReferenceChanged
  ) {
    props.setIsSearchExecuted(true);
    claim[CLAIM.IS_PROVISIONAL] = CHOICE.YES;
    claim[CLAIM.PROVISIONAL_CLAIM_REFERENCE] = provisionalClaimReference;
  }

  const [
    expandClaimCircumstancesDrawer,
    setExpandClaimCircumstancesDrawer,
  ] = React.useState(false);
  const isCourtManager = userPrivileges.userHasPrivilege(
    PRIVILEGES.ISCOURTMANAGER,
  );

  const updateFormByProfile = () => {
    if (props.isManager) {
      return setExpandClaimCircumstancesDrawer(!expandClaimCircumstancesDrawer);
    }
    return isCourtManager
      ? pushTo(PAGES.PREOPENING_MANAGER, {
          isFromSagis: true,
          sagisInfoClaim: get(props, 'location.state.sagisInfoClaim'),
        })()
      : isPreOpeningManager
      ? pushTo(PAGES.PREOPEN_CLAIM_PREOPEN_MANAGER, {
          isUsageOpening: get(props, 'location.state.isUsageOpening', false),
        })()
      : pushTo(PAGES.PREOPENING)();
  };

  const closeClaimCircumstancesDrawer = () => {
    return setExpandClaimCircumstancesDrawer(false);
  };

  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);
  const { message } = displayErrorText(
    props.claimCircumstancesValidation,
    translateArrayErrorMessages(props.claimCircumstancesValidation, true),
  );

  const isCLaimOpeningManagerPending =
    userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER) &&
    featureService.hasFeature(FEATURES.UPDATE_CLAIM_OPENING_MANAGER) &&
    isPending;

  const [loading, setLoading] = useState(false);
  const successCallback = () => {
    setLoading(false);
    props.setIsSearchExecuted(true);
  };
  const handleSearch = () => {
    if (!claim[CLAIM.PROVISIONAL_CLAIM_REFERENCE]) return;
    setLoading(true);
    dispatch(
      findProvisionalClaimByReference(
        claim[CLAIM.PROVISIONAL_CLAIM_REFERENCE],
        successCallback,
      ),
    );
  };

  const getIsProvisionalFormValue = () => {
    if (
      !props.isRecapPage ||
      (props.isRecapPage && isProvisionalReferenceChanged)
    ) {
      return get(claim, [CLAIM.IS_PROVISIONAL], '');
    }

    if (
      (props.isRecapPage && get(claim, [CLAIM.PROVISIONAL_CLAIM_REFERENCE])) ||
      (!isProvisionalReferenceChanged &&
        get(claim, [CLAIM.PROVISIONAL_CLAIM_REFERENCE])) ||
      get(claim, [CLAIM.IS_PROVISIONAL], '') === CHOICE.YES
    ) {
      return CHOICE.YES;
    }

    if (
      !isProvisionalReferenceChanged &&
      (get(claim, [CLAIM.PROVISIONAL_CLAIM_REFERENCE]) ||
        get(claim, [CLAIM.IS_PROVISIONAL], '') === CHOICE.YES)
    ) {
      return CHOICE.YES;
    }
    return CHOICE.NO;
  };
  const isInputDisabled = isCLaimOpeningManagerPending
    ? !isCLaimOpeningManagerPending
    : props.disableInput
    ? props.disableInput
    : props.isConsultation;
  return (
    <Container className="container">
      <Card>
        <CardContent>
          <Typography className="title">
            {translate({ ...messages.claimCircumstancesTitle }, props)}
          </Typography>

          <GridWrapper>
            <Grid container spacing={5}>
              {(isPreOpeningManager || props.isBasicManagement) && (
                <Grid item xs>
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="management-mode"
                      className="label"
                    >
                      {translate({ ...messages.managementMode }, props)}
                    </InputLabel>
                    <Select
                      name="management-mode"
                      value={String(get(claim, [CLAIM.MANAGEMENT_MODE]))}
                      onChange={handleChange(CLAIM.MANAGEMENT_MODE)}
                    >
                      {!isEmpty(get(props.listings, 'modeGestion', [])) &&
                        get(props.listings, 'modeGestion', [])
                          .filter(
                            (mode: {
                              ItemKey: string;
                              ItemDescription: string;
                            }) => mode.ItemKey != '2',
                          )
                          .map(
                            (mode: {
                              ItemKey: string;
                              ItemDescription: string;
                            }) => (
                              <MenuItem value={mode.ItemKey} key={mode.ItemKey}>
                                {mode.ItemDescription}
                              </MenuItem>
                            ),
                          )}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {((isPreOpeningManager || props.isBasicManagement) &&
                get(claim, [CLAIM.MANAGEMENT_MODE]) ===
                  MANAGEMENT_MODES.COMMON_RIGHT) ||
              get(claim, [CLAIM.MANAGEMENT_MODE]) ===
                MANAGEMENT_MODES.FOREIGN ? (
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="scaleCase"
                      className="label"
                    >
                      {translate({ ...messages.scaleCase }, props)}
                    </InputLabel>
                    <Select
                      name={CLAIM.SCALE_CASE_KEY}
                      value={get(claim, [CLAIM.SCALE_CASE_KEY], null)}
                      onChange={handleChange(CLAIM.SCALE_CASE_KEY)}
                      disabled={isCLaimOpeningManagerPending}
                    >
                      {!isEmpty(get(props.listings, 'casDeBareme', [])) &&
                        get(props.listings, 'casDeBareme', []).map(
                          (scaleCase: SagisConfig) => (
                            <MenuItem
                              value={scaleCase.ItemKey}
                              key={scaleCase.ItemKey}
                            >
                              {scaleCase?.ItemDescription}
                            </MenuItem>
                          ),
                        )}
                    </Select>
                  </FormControl>
                </Grid>
              ) : (
                !(isPreOpeningManager || props.isBasicManagement) && (
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <InputLabel
                        ref={inputLabel}
                        id="scaleCase"
                        className="label"
                      >
                        {translate({ ...messages.scaleCase }, props)}
                      </InputLabel>
                      <Select
                        name={CLAIM.SCALE_CASE_KEY}
                        value={get(claim, [CLAIM.SCALE_CASE_KEY], null)}
                        onChange={handleChange(CLAIM.SCALE_CASE_KEY)}
                        disabled={isCLaimOpeningManagerPending}
                      >
                        {!isEmpty(get(props.listings, 'casDeBareme', [])) &&
                          get(props.listings, 'casDeBareme', []).map(
                            (scaleCase: SagisConfig) => (
                              <MenuItem
                                value={scaleCase.ItemKey}
                                key={scaleCase.ItemKey}
                              >
                                {scaleCase?.ItemDescription}
                              </MenuItem>
                            ),
                          )}
                      </Select>
                    </FormControl>
                  </Grid>
                )
              )}
              <Grid item xs={3}>
                {get(claim, [CLAIM.SCALE_CASE_KEY]) && (
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="circumstanceCode"
                      className="label"
                    >
                      {translate({ ...messages.circumstance }, props)}
                    </InputLabel>
                    <Select
                      name={CLAIM.ACCIDENT_CIRCONSTANCE_KEY}
                      value={get(claim, [CLAIM.ACCIDENT_CIRCONSTANCE_KEY])}
                      onChange={handleChange(CLAIM.ACCIDENT_CIRCONSTANCE_KEY)}
                      disabled={isCLaimOpeningManagerPending}
                    >
                      {!isEmpty(getCircumstances()) &&
                        getCircumstances().map((circumstance: SagisConfig) => (
                          <MenuItem
                            value={circumstance.ItemKey}
                            key={circumstance.ItemKey}
                          >
                            {circumstance.ItemDescription}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>
              {isScaleCaseOther ||
                (!isEmpty(claim[CLAIM.ACCIDENT_CIRCONSTANCE]) &&
                  get(claim, [CLAIM.ACCIDENT_CIRCONSTANCE_KEY]) && (
                    <Grid item xs={3}>
                      <TextField
                        onChange={handleChange(CLAIM.ACCIDENT_CIRCONSTANCE)}
                        name={CLAIM.ACCIDENT_CIRCONSTANCE}
                        value={get(claim, [CLAIM.ACCIDENT_CIRCONSTANCE])}
                        label={translate(
                          { ...messages.circumstanceDetail },
                          props,
                        )}
                        fullWidth
                        multiline
                        disabled={isCLaimOpeningManagerPending}
                      />
                    </Grid>
                  ))}

              <Grid item xs={3}>
                {get(claim, [CLAIM.ACCIDENT_CIRCONSTANCE_KEY]) && (
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="responsibility"
                      className="label"
                    >
                      {translate({ ...messages.responsibility }, props)}
                    </InputLabel>
                    <Select
                      name={CLAIM.RESPONSABILITY_KEY}
                      value={
                        featureService.hasFeature(
                          FEATURES.MIGRATION_CLAIM_INFO,
                        ) && !isEmpty(props.claimCircumstancesSagis)
                          ? get(
                              props.claimCircumstancesSagis,
                              'responsibilityRateCode',
                            )
                          : get(claim, [CLAIM.RESPONSABILITY_KEY])
                      }
                      onChange={handleChange(CLAIM.RESPONSABILITY_KEY)}
                      disabled={!isScaleCaseOther}
                    >
                      {!isEmpty(get(props.listings, 'responsability', [])) &&
                        get(props.listings, 'responsability', []).map(
                          (responsability: SagisConfig) => (
                            <MenuItem
                              value={responsability.ItemKey}
                              key={responsability.ItemKey}
                            >
                              {responsability.ItemDescription}
                            </MenuItem>
                          ),
                        )}
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={3}>
                {get(claim, [CLAIM.RESPONSABILITY_KEY]) && (
                  <TextField
                    id="standard-basic-desc"
                    onChange={handleChange(CLAIM.ACCIDENT_DESCRIPTION)}
                    name={CLAIM.ACCIDENT_DESCRIPTION}
                    value={get(claim, [CLAIM.ACCIDENT_DESCRIPTION])}
                    label={translate({ ...messages.description }, props)}
                    fullWidth
                    InputProps={props.inputProps}
                    disabled={
                      props.disableInput || isCLaimOpeningManagerPending
                    }
                    InputLabelProps={{ shrink: props.inputLabelProps }}
                    multiline
                    error={
                      !!get(props.claimCircumstancesValidation, [
                        CLAIM.ACCIDENT_DESCRIPTION,
                      ])
                    }
                    helperText={message[CLAIM.ACCIDENT_DESCRIPTION]}
                  />
                )}
              </Grid>
            </Grid>
          </GridWrapper>
          <div className="checkdiv">
            <Typography className="text">
              {translate({ ...messages.legalProcedure }, props)}
            </Typography>
            {!props.isRecapPage ||
            (userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER) &&
              featureService.hasFeature(
                FEATURES.UPDATE_CLAIM_OPENING_MANAGER,
              ) &&
              isPending) ? (
              <FormControl>
                <RadioGroup
                  value={
                    !props.isRecapPage
                      ? get(claim, [CLAIM.IS_JUDICIAL_PROCEDURE], '')
                      : get(claim, [CLAIM.JUDICIAL_PROCEDURE_ID]) ||
                        get(claim, [CLAIM.IS_JUDICIAL_PROCEDURE], '') ===
                          CHOICE.YES
                      ? CHOICE.YES
                      : CHOICE.NO
                  }
                  onChange={handleChange(CLAIM.IS_JUDICIAL_PROCEDURE)}
                  row
                >
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.YES}
                        checkedIcon={<CheckCircle />}
                      />
                    }
                    label={translate({ ...messages.yes }, props)}
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        value={CHOICE.NO}
                        checkedIcon={<CheckCircle />}
                      />
                    }
                    label={translate({ ...messages.no }, props)}
                  />
                </RadioGroup>
                {!!get(props.claimCircumstancesValidation, [
                  CLAIM.IS_JUDICIAL_PROCEDURE,
                ]) && (
                  <FormHelperText className="error">
                    {message[CLAIM.IS_JUDICIAL_PROCEDURE]}
                  </FormHelperText>
                )}
              </FormControl>
            ) : (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={true}
                    checkedIcon={<CheckCircle />}
                    icon={<RadioButtonUnchecked />}
                    disabled={props.disableInput}
                  />
                }
                label={
                  !!get(claim, [CLAIM.JUDICIAL_PROCEDURE_ID])
                    ? translate({ ...messages.yes }, props)
                    : translate({ ...messages.no }, props)
                }
              />
            )}
          </div>
          <Collapse
            in={
              !props.isRecapPage
                ? get(claim, [CLAIM.IS_JUDICIAL_PROCEDURE], '') === CHOICE.YES
                : !!get(claim, [CLAIM.JUDICIAL_PROCEDURE_ID], '') ||
                  get(claim, [CLAIM.IS_JUDICIAL_PROCEDURE], '') === CHOICE.YES
            }
          >
            {featureService.hasFeature(FEATURES.BASIC_MANAGEMENT) &&
              props.isBasicManagement &&
              props.handleChangeProcedureType &&
              props.handleChangeProcedureNature && (
                <>
                  <GridWrapper>
                    <Grid container spacing={2}>
                      <Grid item xs>
                        <FormControl fullWidth>
                          <InputLabel className="label">
                            {translate({ ...messages.procedureType }, props)}
                          </InputLabel>
                          <Select
                            name="procedureType"
                            value={props.procedureType}
                            onChange={props.handleChangeProcedureType}
                          >
                            <MenuItem value={0}>
                              {PROCEDURE_TYPES.FIRST_INSTANCE}
                            </MenuItem>
                            <MenuItem value={1}>
                              {PROCEDURE_TYPES.APPEAL}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs>
                        <FormControl fullWidth>
                          <InputLabel className="label">
                            {translate({ ...messages.procedureType }, props)}
                          </InputLabel>
                          <Select
                            labelId="procedure_nature-label-id"
                            onChange={props.handleChangeProcedureNature}
                            name="procedureNature"
                            value={props.procedureNatureId}
                          >
                            {!isEmpty(
                              get(props.listings, 'procedureNature', []),
                            ) &&
                              get(props.listings, 'procedureNature', []).map(
                                (el: Config) => (
                                  <MenuItem key={el.id} value={String(el.id)}>
                                    {el.title}
                                  </MenuItem>
                                ),
                              )}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </GridWrapper>
                </>
              )}
            {featureService.hasFeature(FEATURES.COURT_CALENDAR) ? (
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormControl>
                    <RadioGroup
                      className="subContainer"
                      aria-label="position"
                      name="position"
                      value={get(claim, [CLAIM.JUDICIAL_PROCEDURE_ID], '')}
                      onChange={handleChange(CLAIM.JUDICIAL_PROCEDURE_ID)}
                      row
                      style={{ marginTop: '16px' }}
                    >
                      {!isEmpty(
                        get(props.listings, 'judicialProcedures', []),
                      ) &&
                        props.listings!.judicialProcedures.map(
                          (procedure: Config) => (
                            <FormControlLabel
                              key={procedure.id}
                              value={procedure.id}
                              classes={{ label: 'radio-label-style' }}
                              control={
                                <Radio
                                  color="primary"
                                  disabled={
                                    isCLaimOpeningManagerPending
                                      ? !isCLaimOpeningManagerPending
                                      : props.disableInput
                                  }
                                />
                              }
                              label={procedure.title}
                              labelPlacement="end"
                              className="input-checkbox"
                            />
                          ),
                        )}
                    </RadioGroup>
                    {!!get(props.claimCircumstancesValidation, [
                      CLAIM.JUDICIAL_PROCEDURE_ID,
                    ]) && (
                      <FormHelperText className="error">
                        {message[CLAIM.JUDICIAL_PROCEDURE_ID]}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel className="label">
                      {translate({ ...messages.procedureType }, props)}
                    </InputLabel>
                    <Select
                      labelId="procedure_nature-label-id"
                      onChange={handleChange(CLAIM.PROCEDURE_NATURE_ID)}
                      name={CLAIM.PROCEDURE_NATURE_ID}
                      value={get(claim, [CLAIM.PROCEDURE_NATURE_ID], '')}
                      disabled={isInputDisabled}
                    >
                      {!isEmpty(get(props.listings, 'procedureNature', [])) &&
                        get(props.listings, 'procedureNature', []).map(
                          (el: Config) => (
                            <MenuItem key={el.id} value={String(el.id)}>
                              {el.title}
                            </MenuItem>
                          ),
                        )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <TextField
                      id="court-number"
                      label={translate({ ...messages.courtNumber }, props)}
                      name={CLAIM.COURT_NUMBER}
                      onChange={handleChange(CLAIM.COURT_NUMBER)}
                      value={get(claim, [CLAIM.COURT_NUMBER], '')}
                      fullWidth
                      InputProps={props.inputProps}
                      disabled={isInputDisabled}
                      InputLabelProps={{ shrink: props.inputLabelProps }}
                      multiline
                      error={
                        !!get(props.claimCircumstancesValidation, [
                          CLAIM.COURT_NUMBER,
                        ]) ||
                        (isCourtNumberFieldChanged &&
                          !validationCourtFileNumber(
                            get(claim, [CLAIM.COURT_NUMBER]),
                          ))
                      }
                      helperText={
                        isCourtNumberFieldChanged &&
                        !validationCourtFileNumber(
                          get(claim, [CLAIM.COURT_NUMBER]),
                        )
                          ? translate(messages.courtFileNumberError, props)
                          : ''
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>
            ) : (
              <FormControl>
                <RadioGroup
                  className="subContainer"
                  aria-label="position"
                  name="position"
                  value={get(claim, [CLAIM.JUDICIAL_PROCEDURE_ID], '')}
                  onChange={handleChange(CLAIM.JUDICIAL_PROCEDURE_ID)}
                  row
                >
                  {!isEmpty(get(props.listings, 'judicialProcedures', [])) &&
                    props.listings!.judicialProcedures.map(
                      (procedure: Config) => (
                        <FormControlLabel
                          key={procedure.id}
                          value={procedure.id}
                          classes={{ label: 'radio-label-style' }}
                          control={
                            <Radio
                              color="primary"
                              disabled={
                                isCLaimOpeningManagerPending
                                  ? !isCLaimOpeningManagerPending
                                  : props.disableInput
                              }
                            />
                          }
                          label={procedure.title}
                          labelPlacement="end"
                          className="input-checkbox"
                        />
                      ),
                    )}
                </RadioGroup>
                {!!get(props.claimCircumstancesValidation, [
                  CLAIM.JUDICIAL_PROCEDURE_ID,
                ]) && (
                  <FormHelperText className="error">
                    {message[CLAIM.JUDICIAL_PROCEDURE_ID]}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          </Collapse>
          {featureService.hasFeature(FEATURES.COURT_CALENDAR) && (
            <>
              <div className="checkdiv">
                <Typography className="text">
                  {translate({ ...messages.provisionalClaim }, props)}
                </Typography>
                {!props.isRecapPage ||
                (userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER) &&
                  featureService.hasFeature(
                    FEATURES.UPDATE_CLAIM_OPENING_MANAGER,
                  ) &&
                  isPending) ? (
                  <FormControl>
                    <RadioGroup
                      value={getIsProvisionalFormValue()}
                      onChange={handleChange(CLAIM.IS_PROVISIONAL)}
                      row
                    >
                      <FormControlLabel
                        control={
                          <Radio
                            color="primary"
                            value={CHOICE.YES}
                            checkedIcon={<CheckCircle />}
                          />
                        }
                        label={translate({ ...messages.yes }, props)}
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            color="primary"
                            value={CHOICE.NO}
                            checkedIcon={<CheckCircle />}
                          />
                        }
                        label={translate({ ...messages.no }, props)}
                      />
                    </RadioGroup>
                  </FormControl>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={true}
                        checkedIcon={<CheckCircle />}
                        icon={<RadioButtonUnchecked />}
                        disabled={props.disableInput}
                      />
                    }
                    label={
                      !!get(claim, [CLAIM.PROVISIONAL_CLAIM_REFERENCE])
                        ? translate({ ...messages.yes }, props)
                        : translate({ ...messages.no }, props)
                    }
                  />
                )}
              </div>
              <Collapse
                in={
                  !props.isRecapPage
                    ? get(claim, [CLAIM.IS_PROVISIONAL], '') === CHOICE.YES
                    : (!!get(claim, [CLAIM.PROVISIONAL_CLAIM_REFERENCE], '') &&
                        get(claim, [CLAIM.IS_PROVISIONAL], '') !== CHOICE.NO) ||
                      get(claim, [CLAIM.IS_PROVISIONAL], '') === CHOICE.YES
                }
              >
                <Box className={classes.row}>
                  <TextField
                    id="provisional-claim-reference"
                    label={translate(
                      { ...messages.provisionalClaimReference },
                      props,
                    )}
                    name={CLAIM.PROVISIONAL_CLAIM_REFERENCE}
                    onChange={handleChange(CLAIM.PROVISIONAL_CLAIM_REFERENCE)}
                    value={get(claim, [CLAIM.PROVISIONAL_CLAIM_REFERENCE], '')}
                    fullWidth
                    InputProps={props.inputProps}
                    disabled={isInputDisabled}
                    InputLabelProps={{ shrink: props.inputLabelProps }}
                    multiline
                    error={
                      !!get(props.claimCircumstancesValidation, [
                        CLAIM.PROVISIONAL_CLAIM_REFERENCE,
                      ])
                    }
                    helperText={message[CLAIM.PROVISIONAL_CLAIM_REFERENCE]}
                    style={{ width: 250 }}
                  />
                  {!isInputDisabled && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearch}
                      disabled={loading}
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <SearchIcon />
                        )
                      }
                      style={{
                        height: 36,
                        whiteSpace: 'nowrap',
                        alignSelf: 'center',
                      }}
                    >
                      {loading
                        ? translate(
                            {
                              ...messages.preOpeningPageProvisionalLoadingSearchButton,
                            },
                            props,
                          )
                        : translate(
                            {
                              ...messages.preOpeningPageProvisionalSearchButton,
                            },
                            props,
                          )}
                    </Button>
                  )}
                </Box>
              </Collapse>
            </>
          )}
        </CardContent>
      </Card>
      <ClaimCircumstancesDrawer
        open={expandClaimCircumstancesDrawer}
        handleToggleDrawer={updateFormByProfile}
        claimToPreopen={value}
        judicialProcedures={get(props.listings, 'judicialProcedures', [])}
        setClaimRecap={props.setClaimRecap}
        closeClaimCircumstancesDrawer={closeClaimCircumstancesDrawer}
      />
    </Container>
  );
};
export default injectIntl(ClaimCircumstances);
